import { useState } from "react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

export default function WordCounter() {

  const [text, setText] =
    useState("");

  /* COUNTS */

  const words =
    text
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;

  const characters =
    text.length;

  const charactersNoSpaces =
    text.replace(/\s/g, "")
      .length;

  const sentences =
    text
      .split(/[.!?]+/)
      .filter(Boolean).length;

  const paragraphs =
    text
      .split(/\n+/)
      .filter(
        (p) => p.trim() !== ""
      ).length;

  const readingTime =
    Math.ceil(words / 200);

  const speakingTime =
    Math.ceil(words / 130);

  /* COPY */

  const copyText = () => {

    navigator.clipboard.writeText(
      text
    );

    alert("Text copied!");
  };

  /* CLEAR */

  const clearText = () => {

    setText("");
  };

  return (
    <>
      <SEO
        title="Free Word Counter Online"
        description="Count words, characters, sentences, paragraphs, reading time, and speaking time instantly online."
        keywords="word counter, character counter, online word counter, text analyzer"
        url="https://online-tools-center.vercel.app/word-counter"
      />

      <Navbar />

      <div className="container py-4">

        <div className="row justify-content-center">

          <div className="col-lg-10">

            <div className="card shadow-lg border-0 rounded-4 p-4">

              {/* HERO */}

              <div className="text-center mb-5">

                <h1 className="fw-bold mb-3">
                  Word Counter
                </h1>

                <p className="text-muted">
                  Count words, characters,
                  sentences, paragraphs,
                  reading time, and more
                  instantly online.
                </p>

                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">

                  <span className="badge bg-light text-dark p-2">
                    ✓ Word Count
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Character Count
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Reading Time
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Free Tool
                  </span>

                </div>

              </div>

              {/* TEXTAREA */}

              <textarea
                rows="10"
                className="form-control mb-4"
                placeholder="Type or paste your text here..."
                value={text}
                onChange={(e) =>
                  setText(
                    e.target.value
                  )
                }
              />

              {/* BUTTONS */}

              <div className="d-flex flex-wrap gap-3 mb-4">

                <button
                  className="btn btn-primary"
                  onClick={copyText}
                >
                  Copy Text
                </button>

                <button
                  className="btn btn-danger"
                  onClick={clearText}
                >
                  Clear Text
                </button>

              </div>

              {/* STATS */}

              <div className="row g-4">

                <div className="col-md-3 col-6">

                  <div className="card border-0 bg-light rounded-4 p-3 text-center h-100">

                    <h6 className="text-muted">
                      Words
                    </h6>

                    <h3 className="fw-bold">
                      {words}
                    </h3>

                  </div>

                </div>

                <div className="col-md-3 col-6">

                  <div className="card border-0 bg-light rounded-4 p-3 text-center h-100">

                    <h6 className="text-muted">
                      Characters
                    </h6>

                    <h3 className="fw-bold">
                      {characters}
                    </h3>

                  </div>

                </div>

                <div className="col-md-3 col-6">

                  <div className="card border-0 bg-light rounded-4 p-3 text-center h-100">

                    <h6 className="text-muted">
                      No Spaces
                    </h6>

                    <h3 className="fw-bold">
                      {charactersNoSpaces}
                    </h3>

                  </div>

                </div>

                <div className="col-md-3 col-6">

                  <div className="card border-0 bg-light rounded-4 p-3 text-center h-100">

                    <h6 className="text-muted">
                      Sentences
                    </h6>

                    <h3 className="fw-bold">
                      {sentences}
                    </h3>

                  </div>

                </div>

              </div>

              {/* EXTRA STATS */}

              <div className="row g-4 mt-1">

                <div className="col-md-4">

                  <div className="card border-0 shadow-sm rounded-4 p-4 text-center h-100">

                    <h6 className="text-muted">
                      Paragraphs
                    </h6>

                    <h3 className="fw-bold">
                      {paragraphs}
                    </h3>

                  </div>

                </div>

                <div className="col-md-4">

                  <div className="card border-0 shadow-sm rounded-4 p-4 text-center h-100">

                    <h6 className="text-muted">
                      Reading Time
                    </h6>

                    <h3 className="fw-bold">
                      {readingTime}
                      {" "}
                      min
                    </h3>

                  </div>

                </div>

                <div className="col-md-4">

                  <div className="card border-0 shadow-sm rounded-4 p-4 text-center h-100">

                    <h6 className="text-muted">
                      Speaking Time
                    </h6>

                    <h3 className="fw-bold">
                      {speakingTime}
                      {" "}
                      min
                    </h3>

                  </div>

                </div>

              </div>

            </div>

            {/* SEO CONTENT */}

           {/* SEO CONTENT - Replace existing mt-5 div with this */}
<div className="mt-5">

  <h2 className="fw-bold mb-3">What is a Word Counter?</h2>
  <p>
    A word counter is an online tool that instantly analyzes any block of text and
    tells you how many words, characters, sentences, and paragraphs it contains.
    Whether you are writing a blog post with a 1,500-word target, crafting a
    college essay with a strict 500-word limit, or optimizing a meta description
    to stay under 160 characters, a word counter removes all the guesswork and
    gives you accurate numbers in real time.
  </p>

  <h2 className="fw-bold mt-5 mb-3">How to Use This Word Counter</h2>
  <p>
    Using our word counter is simple and requires no signup or download. Follow
    these steps to analyze your text in seconds:
  </p>
  <ol>
    <li className="mb-2">
      <strong>Paste or type your text</strong> into the large text box above.
      You can paste content from a document, email, blog draft, or any source.
    </li>
    <li className="mb-2">
      <strong>View instant results</strong> — word count, character count,
      sentence count, paragraph count, reading time, and speaking time update
      automatically as you type.
    </li>
    <li className="mb-2">
      <strong>Copy or clear</strong> your text using the buttons provided once
      you have noted your results.
    </li>
  </ol>
  <p>
    No button press is needed — all counts update live as you type or paste text.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Why Reading Time and Speaking Time Matter</h2>
  <p>
    Our tool goes beyond basic word and character counts by also estimating your
    reading time and speaking time. Reading time is calculated at an average of
    200 words per minute, which is the typical adult reading speed. Speaking time
    is calculated at 130 words per minute, reflecting a comfortable speaking pace
    used in presentations and podcasts. These estimates help content creators
    plan YouTube scripts, podcast episodes, speeches, and blog posts so they hit
    their target duration without over- or under-writing.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Who Uses a Word Counter?</h2>
  <p>
    Word counters are used by a wide range of people across different fields.
    Students use them to meet minimum and maximum word limits on essays and
    assignments. Bloggers and content writers use them to hit SEO targets, since
    longer, well-structured articles tend to rank better on Google. Social media
    managers use them to ensure captions stay within platform character limits —
    for example, Twitter/X allows 280 characters and LinkedIn posts perform best
    under 1,300 characters. SEO professionals check meta descriptions and title
    tags for correct length. Authors and novelists track chapter word counts and
    overall manuscript progress. Even email marketers use word counters to keep
    subject lines short and punchy.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Character Limits for Popular Platforms</h2>
  <p>
    Knowing your character count is essential when writing for specific platforms.
    Here are the key limits to keep in mind:
  </p>
  <ul>
    <li className="mb-2"><strong>Google Meta Description:</strong> 150–160 characters</li>
    <li className="mb-2"><strong>Google Title Tag:</strong> 50–60 characters</li>
    <li className="mb-2"><strong>Twitter / X Post:</strong> 280 characters</li>
    <li className="mb-2"><strong>Instagram Caption:</strong> 2,200 characters</li>
    <li className="mb-2"><strong>LinkedIn Post:</strong> 3,000 characters</li>
    <li className="mb-2"><strong>YouTube Description:</strong> 5,000 characters</li>
    <li className="mb-2"><strong>Facebook Post:</strong> 63,206 characters</li>
  </ul>
  <p>
    Use our character counter to make sure your content fits perfectly before
    publishing.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Frequently Asked Questions</h2>

  <div className="mb-4">
    <h5>Is this word counter free to use?</h5>
    <p>
      Yes, our online word counter is completely free with no signup, no login,
      and no download required. You can use it unlimited times from any device
      including mobile, tablet, and desktop.
    </p>
  </div>

  <div className="mb-4">
    <h5>Does the tool count characters with and without spaces?</h5>
    <p>
      Yes. The tool displays two separate character counts — one that includes
      all spaces and one that excludes them. This is useful when platforms specify
      character limits that do or do not count spaces.
    </p>
  </div>

  <div className="mb-4">
    <h5>How is reading time calculated?</h5>
    <p>
      Reading time is estimated based on an average adult reading speed of 200
      words per minute. So a 1,000-word article would show an estimated reading
      time of 5 minutes. This is an approximation and may vary based on content
      complexity.
    </p>
  </div>

  <div className="mb-4">
    <h5>Does the tool store my text?</h5>
    <p>
      No. All text analysis happens entirely inside your browser. Your content is
      never sent to any server, so your data remains completely private and secure.
    </p>
  </div>

  <div className="mb-4">
    <h5>Can I use this for checking essay word limits?</h5>
    <p>
      Absolutely. Students widely use this tool to verify essay and assignment
      word counts before submission. Simply paste your essay text into the box
      and the word count is shown instantly.
    </p>
  </div>

  {/* RELATED TOOLS */}
  <div className="mt-5 p-4 bg-light rounded-4">
    <h3 className="fw-bold mb-3">Related Tools</h3>
    <div className="d-flex flex-wrap gap-3">
      <a href="/json-formatter" className="btn btn-outline-primary">JSON Formatter</a>
      <a href="/password-generator" className="btn btn-outline-primary">Password Generator</a>
      <a href="/qr-generator" className="btn btn-outline-primary">QR Generator</a>
    </div>
  </div>

</div>

          </div>

        </div>

      </div>

      <Footer />

    </>
  );
}